import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useExam } from "../context/ExamContext";
import {
  getProfile,
  updateUserProfile,
  changePassword,
} from "../api/client";

import AppSidebar from "../pages/AppSidebar";

export default function Profile() {
  const { currentUser, setCurrentUser, logout } = useExam();
  const navigate = useNavigate();

  // ------------------------------------------------------------
  // Profile loading
  // ------------------------------------------------------------

  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState("");

  const [profile, setProfile] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadProfile = async () => {
      setLoading(true);
      setProfileError("");

      try {
        const { data } = await getProfile();

        if (!mounted) return;

        setProfile(data);
        setCurrentUser(data);
      } catch (err) {
        if (!mounted) return;

        const message =
          err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          "Could not load profile.";

        setProfileError(message);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      mounted = false;
    };
  }, [setCurrentUser]);

  // Use freshly loaded backend profile first.
  const user = profile || currentUser;

  // Django returns:
  // courses: [{ id: 1, name: "CMA Part 1" }]
  const courses = Array.isArray(user?.courses)
    ? user.courses
    : [];

  const courseNames = courses.map((course) => {
    if (typeof course === "string") {
      return course;
    }

    return course?.name || "";
  }).filter(Boolean);

  const infoItems = [
    {
      label: "Student ID",
      value: user?.id,
      icon: "bi-person-vcard",
    },
    {
      label: "Mobile Number",
      value: user?.mobile,
      icon: "bi-telephone",
    },
    {
      label: "Email",
      value: user?.email,
      icon: "bi-envelope",
    },
    {
      label: "Course(s)",
      value: courseNames.join(", "),
      icon: "bi-mortarboard",
    },
  ];

  // ------------------------------------------------------------
  // Edit profile
  // ------------------------------------------------------------

  const [showEdit, setShowEdit] = useState(false);

  const [editForm, setEditForm] = useState({
    fullName: "",
    mobile: "",
  });

  const [editError, setEditError] = useState("");
  const [editBusy, setEditBusy] = useState(false);

  const openEdit = () => {
    setEditForm({
      fullName: user?.full_name || user?.fullName || "",
      mobile: user?.mobile || "",
    });

    setEditError("");
    setShowEdit(true);
  };

  const saveEdit = async () => {
    setEditError("");

    if (!editForm.fullName.trim()) {
      setEditError("Name cannot be empty.");
      return;
    }

    setEditBusy(true);

    try {
      const { data } = await updateUserProfile({
        full_name: editForm.fullName.trim(),
        mobile: editForm.mobile.trim(),
      });

      setProfile(data);
      setCurrentUser(data);

      setShowEdit(false);
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.message ||
        "Could not update profile.";

      setEditError(message);
    } finally {
      setEditBusy(false);
    }
  };

  // ------------------------------------------------------------
  // Change password
  // ------------------------------------------------------------

  const [showPassword, setShowPassword] = useState(false);

  const [pwForm, setPwForm] = useState({
    current: "",
    next: "",
    confirm: "",
  });

  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const openPassword = () => {
    setPwForm({
      current: "",
      next: "",
      confirm: "",
    });

    setPwError("");
    setPwSuccess("");
    setShowPassword(true);
  };

  const savePassword = async () => {
    setPwError("");
    setPwSuccess("");

    if (!pwForm.current || !pwForm.next || !pwForm.confirm) {
      setPwError("Please fill in all fields.");
      return;
    }

    if (pwForm.next.length < 6) {
      setPwError("New password must be at least 6 characters.");
      return;
    }

    if (pwForm.next !== pwForm.confirm) {
      setPwError("New passwords do not match.");
      return;
    }

    setPwBusy(true);

    try {
      await changePassword({
        current_password: pwForm.current,
        new_password: pwForm.next,
      });

      setPwSuccess("Password updated successfully.");

      setPwForm({
        current: "",
        next: "",
        confirm: "",
      });
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.message ||
        "Could not change password.";

      setPwError(message);
    } finally {
      setPwBusy(false);
    }
  };

  // ------------------------------------------------------------
  // Logout
  // ------------------------------------------------------------

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // ------------------------------------------------------------
  // Loading
  // ------------------------------------------------------------

  if (loading) {
    return (
      <div className="oes-page-full">
        <div className="oes-home-shell d-flex flex-row overflow-hidden">
          <AppSidebar />

          <div className="flex-grow-1 p-3 p-md-4">
            <div className="d-flex justify-content-center align-items-center py-5">
              <div className="text-center">
                <div
                  className="spinner-border text-primary mb-3"
                  role="status"
                />
                <div className="text-muted">
                  Loading profile...
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="oes-page-full">
      <div className="oes-home-shell d-flex flex-row overflow-hidden">

        <AppSidebar />

        <div className="flex-grow-1 p-3 p-md-4">

          {/* -------------------------------------------------- */}
          {/* Header */}
          {/* -------------------------------------------------- */}

          <div className="d-flex justify-content-between align-items-start mb-3">

            <button
              className="btn btn-link btn-sm text-secondary text-decoration-none ps-0"
              onClick={() => navigate("/home")}
            >
              <i className="bi bi-arrow-left me-1" />
              Back to Home
            </button>

            <div className="d-flex align-items-center gap-3">

              <i className="bi bi-bell fs-5 text-secondary" />

              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleLogout}
              >
                <i className="bi bi-box-arrow-right me-1" />
                Logout
              </button>

            </div>
          </div>

          {/* -------------------------------------------------- */}
          {/* Error */}
          {/* -------------------------------------------------- */}

          {profileError && (
            <div className="alert alert-danger small">
              {profileError}
            </div>
          )}

          {/* -------------------------------------------------- */}
          {/* Profile Card */}
          {/* -------------------------------------------------- */}

          <div className="d-flex justify-content-center">

            <div className="profile-card">

              <div className="profile-banner" />

              <div className="profile-avatar">
                <i className="bi bi-person-fill" />
              </div>

              <div className="text-center px-3 px-md-4 pb-4">

                <h2 className="h5 mb-1 mt-2">
                  {user?.full_name ||
                    user?.fullName ||
                    "Student"}
                </h2>

                <p className="text-muted small mb-2">
                  {user?.email || "—"}
                </p>

                {/* Courses */}

                {courseNames.length > 0 && (
                  <div className="d-flex flex-wrap justify-content-center gap-2">

                    {courses.map((course, index) => {
                      const courseName =
                        typeof course === "string"
                          ? course
                          : course?.name;

                      const courseId =
                        typeof course === "object"
                          ? course?.id
                          : course;

                      return (
                        <span
                          className="badge oes-course-badge"
                          key={courseId || index}
                        >
                          {courseName}
                        </span>
                      );
                    })}

                  </div>
                )}

                {/* Information */}

                <div className="profile-info-grid mt-4 text-start">

                  {infoItems.map((item) => (
                    <div
                      className="profile-info-item"
                      key={item.label}
                    >

                      <span className="profile-info-icon">
                        <i className={`bi ${item.icon}`} />
                      </span>

                      <div>
                        <div className="profile-info-label">
                          {item.label}
                        </div>

                        <div className="profile-info-value">
                          {item.value || "—"}
                        </div>
                      </div>

                    </div>
                  ))}

                </div>

                {/* Buttons */}

                <div className="d-flex gap-2 justify-content-center mt-4">

                  <button
                    className="btn btn-outline-primary btn-sm px-3"
                    onClick={openEdit}
                  >
                    <i className="bi bi-pencil-square me-1" />
                    Edit Profile
                  </button>

                  <button
                    className="btn btn-outline-secondary btn-sm px-3"
                    onClick={openPassword}
                  >
                    <i className="bi bi-shield-lock me-1" />
                    Change Password
                  </button>

                </div>

              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ====================================================== */}
      {/* Edit Profile Modal */}
      {/* ====================================================== */}

      {showEdit && (
        <div
          className="modal d-block"
          tabIndex={-1}
          style={{
            background: "rgba(0,0,0,0.45)",
          }}
        >

          <div className="modal-dialog modal-dialog-centered">

            <div className="modal-content">

              <div className="modal-header">

                <h5 className="modal-title">
                  Edit Profile
                </h5>

                <button
                  className="btn-close"
                  onClick={() => setShowEdit(false)}
                />

              </div>

              <div className="modal-body">

                <div className="mb-3">

                  <label className="form-label small text-muted">
                    Full Name
                  </label>

                  <input
                    className="form-control"
                    value={editForm.fullName}
                    onChange={(e) =>
                      setEditForm((form) => ({
                        ...form,
                        fullName: e.target.value,
                      }))
                    }
                  />

                </div>

                <div className="mb-2">

                  <label className="form-label small text-muted">
                    Mobile Number
                  </label>

                  <input
                    className="form-control"
                    value={editForm.mobile}
                    onChange={(e) =>
                      setEditForm((form) => ({
                        ...form,
                        mobile: e.target.value,
                      }))
                    }
                  />

                </div>

                {editError && (
                  <div className="text-danger small mt-2">
                    {editError}
                  </div>
                )}

              </div>

              <div className="modal-footer">

                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setShowEdit(false)}
                >
                  Cancel
                </button>

                <button
                  className="btn btn-primary"
                  onClick={saveEdit}
                  disabled={editBusy}
                >
                  {editBusy
                    ? "Saving..."
                    : "Save Changes"}
                </button>

              </div>

            </div>
          </div>
        </div>
      )}

      {/* ====================================================== */}
      {/* Change Password Modal */}
      {/* ====================================================== */}

      {showPassword && (
        <div
          className="modal d-block"
          tabIndex={-1}
          style={{
            background: "rgba(0,0,0,0.45)",
          }}
        >

          <div className="modal-dialog modal-dialog-centered">

            <div className="modal-content">

              <div className="modal-header">

                <h5 className="modal-title">
                  Change Password
                </h5>

                <button
                  className="btn-close"
                  onClick={() => setShowPassword(false)}
                />

              </div>

              <div className="modal-body">

                <div className="mb-3">

                  <label className="form-label small text-muted">
                    Current Password
                  </label>

                  <input
                    type="password"
                    className="form-control"
                    value={pwForm.current}
                    onChange={(e) =>
                      setPwForm((form) => ({
                        ...form,
                        current: e.target.value,
                      }))
                    }
                  />

                </div>

                <div className="mb-3">

                  <label className="form-label small text-muted">
                    New Password
                  </label>

                  <input
                    type="password"
                    className="form-control"
                    value={pwForm.next}
                    onChange={(e) =>
                      setPwForm((form) => ({
                        ...form,
                        next: e.target.value,
                      }))
                    }
                  />

                </div>

                <div className="mb-2">

                  <label className="form-label small text-muted">
                    Confirm New Password
                  </label>

                  <input
                    type="password"
                    className="form-control"
                    value={pwForm.confirm}
                    onChange={(e) =>
                      setPwForm((form) => ({
                        ...form,
                        confirm: e.target.value,
                      }))
                    }
                  />

                </div>

                {pwError && (
                  <div className="text-danger small mt-2">
                    {pwError}
                  </div>
                )}

                {pwSuccess && (
                  <div className="text-success small mt-2">
                    {pwSuccess}
                  </div>
                )}

              </div>

              <div className="modal-footer">

                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setShowPassword(false)}
                >
                  Close
                </button>

                <button
                  className="btn btn-primary"
                  onClick={savePassword}
                  disabled={pwBusy}
                >
                  {pwBusy
                    ? "Updating..."
                    : "Update Password"}
                </button>

              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
}